import { useMemo } from 'react';
import { useTranslation } from 'translations/hooks';

import {
  DefectReportHistoryDate,
  DefectRange,
} from 'business/quality-control/pages/defect-report-history/providers/filters';
import {
  DefectReportTypeListV1OutputDto,
  IndicatorDto,
} from 'generated/apiSchemas';

import { DefectReportStatusSearchable } from './data/types';

export const useFilterOptions = (
  defectReportData: DefectReportTypeListV1OutputDto | undefined,
  indicators: IndicatorDto[] = [],
) => {
  const { t } = useTranslation();

  const defectTypeOptions = useMemo(() => {
    const options =
      defectReportData?.defectReportTypes?.map((type) => ({
        label: type.label,
        value: type.id,
      })) || [];

    return options;
  }, [defectReportData]);

  const indicatorOptions = useMemo(
    () =>
      indicators.map((indicator) => ({
        label: indicator.label,
        value: indicator.id,
      })),
    [indicators],
  );

  const defectReportDateOptions = useMemo(() => {
    return Object.values(DefectReportHistoryDate).map((date) => ({
      label: t(`input.defectReportDate.selectOptions.option`, {
        context: date,
      }),
      value: date,
    }));
  }, [t]);

  const statusOptions = useMemo(() => {
    return Object.values(DefectReportStatusSearchable).map((status) => ({
      label: t(`qualityControl.defectReportHistory.statusSearchable.state`, {
        context: status,
      }),
      value: status,
    }));
  }, [t]);

  const defectRangeOptions = useMemo(() => {
    return Object.values(DefectRange).map((range) => ({
      label: t(`input.defectRange.selectOptions.option`, {
        context: range,
      }),
      value: range,
    }));
  }, [t]);

  return {
    defectTypeOptions,
    indicatorOptions,
    defectReportDateOptions,
    statusOptions,
    defectRangeOptions,
  };
};
